import React from "react";
import { SkeletonBlock } from "./Skeleton";

/**
 * Trade / chart screen skeleton.
 *
 * Mirrors `DesktopChartLayout` zone for zone, so the page has its real shape
 * from the first paint instead of one big grey slab:
 *
 *   ┌ toolbar (symbol · period · type · Overlays · Indicators · Alert · share) ┐
 *   │ ┌──┬──────────────────────────────────────┬─ right rail (w-80) ───────┐ │
 *   │ │left│            chart area              │ Zone 1  watchlist        │ │
 *   │ │tool│                                    │ ─────────────────────────│ │
 *   │ │bar │                                    │ Zone 2  quote + ticket   │ │
 *   │ │w-10│                                    │ ─────────────────────────│ │
 *   │ └──┴─ bottom bar (1min…1M · Volume · Price)┤ Zone 3  disclaimer       │ │
 *   └──────────────────────────────────────────────────────────────────────┘
 *
 * What is real and what is grey:
 *   • All static furniture — borders, the left tool strip, timeframe labels,
 *     column headings, Sell/Buy, Limit/Market/Stop, field labels, the
 *     disclaimer — renders for REAL. None of it depends on data, so greying it
 *     out would only make the page look broken.
 *   • Buttons render in their existing unavailable state and accept no input
 *     until the data they act on is here (Place Order especially).
 *   • Only genuine data is grey: the symbol, the chart, the watchlist rows and
 *     their prices, the quote block, and the ticket's Shares/Price/Total values.
 *
 * No price, %, or "$0.00" anywhere, and no green/red: on a trading screen a
 * placeholder number is indistinguishable from a real quote.
 */

/* ── Left vertical tool strip (real: w-10, border-r, 10 tool slots) ── */
const LeftToolbarChrome = () => (
  <div className="flex flex-col items-center gap-1 py-2 border-r border-gray-200 bg-white w-10 shrink-0">
    {Array.from({ length: 10 }).map((_, i) => (
      <div key={i} className="w-10 h-10 flex items-center justify-center">
        <SkeletonBlock width={18} height={18} rounded="rounded" />
      </div>
    ))}
  </div>
);

/* ── Top toolbar. Only the symbol pill is data. ── */
const ToolbarChrome = () => (
  <div className="flex items-center gap-3 bg-white pr-2 pl-0 shrink-0">
    <div className="w-10 h-10 flex items-center justify-center border-r border-gray-200">
      <SkeletonBlock width={16} height={16} rounded="rounded" />
    </div>
    {/* Symbol pill — the ticker itself is data. */}
    <SkeletonBlock width={92} height={32} rounded="rounded-full" />
    <SkeletonBlock width={44} height={36} rounded="rounded" />
    <SkeletonBlock width={36} height={36} rounded="rounded" />
    {["Overlays", "Indicators", "Alert"].map((label) => (
      <button
        key={label}
        type="button"
        disabled
        aria-disabled="true"
        className="px-3 h-9 flex items-center gap-2 rounded border border-gray-200 text-gray-400 text-sm"
      >
        {label}
      </button>
    ))}
    <div className="flex-1" />
    <div className="w-10 h-10 flex items-center justify-center">
      <SkeletonBlock width={18} height={18} rounded="rounded" />
    </div>
  </div>
);

/* ── Bottom bar: real timeframe labels + real toggles, all inert. ── */
const PERIODS = ["1min", "2min", "5min", "15min", "30min", "1h", "4h", "1D", "1W", "1M"];

const BottomBarChrome = () => (
  <div className="flex items-center justify-between px-4 py-0 border-t border-gray-200 bg-white shrink-0">
    <div className="flex items-center gap-1">
      {PERIODS.map((p) => (
        <button
          key={p}
          type="button"
          disabled
          aria-disabled="true"
          className="px-3 h-8 rounded text-xs font-semibold text-gray-400"
        >
          {p}
        </button>
      ))}
    </div>
    <div className="flex items-center gap-2">
      <span className="px-3 h-8 flex items-center text-xs font-semibold text-gray-400">
        Volume OFF
      </span>
      <div className="w-px h-5 bg-gray-300" aria-hidden="true" />
      <span className="px-3 h-8 flex items-center text-xs font-semibold text-gray-400">
        Price Labels OFF
      </span>
    </div>
  </div>
);

/* ── Right rail, three zones — matches ChartRightWatchlist exactly. ── */
const RIGHT_COLS = "grid grid-cols-[1fr_70px_60px_60px] items-center px-3";

const RightRailSkeleton = () => (
  <aside className="hidden lg:flex flex-col h-full min-h-0 overflow-hidden border-l border-gray-200 bg-white w-80 shrink-0">
    {/* Zone 1 — watchlist */}
    <div className="flex flex-col flex-1 min-h-0">
      <div className="flex items-center justify-between px-3 h-10 shrink-0 border-b border-gray-200">
        {/* The list's name is data. */}
        <SkeletonBlock width={124} height={13} />
        <SkeletonBlock width={20} height={20} rounded="rounded-full" />
      </div>
      {/* Column headings are static — render them. */}
      <div
        className={`${RIGHT_COLS} shrink-0 py-1.5 text-[11px] font-semibold text-gray-400 border-b border-gray-200`}
      >
        <span>Symbol</span>
        <span className="text-right">Last</span>
        <span className="text-right">Chg</span>
        <span className="text-right">Chg%</span>
      </div>
      <div className="flex-1 min-h-0 overflow-hidden">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className={`${RIGHT_COLS} py-2.5`} aria-hidden="true">
            <SkeletonBlock width={52} height={11} />
            <div className="flex justify-end">
              <SkeletonBlock width={44} height={11} />
            </div>
            <div className="flex justify-end">
              <SkeletonBlock width={36} height={11} />
            </div>
            <div className="flex justify-end">
              <SkeletonBlock width={40} height={11} />
            </div>
          </div>
        ))}
      </div>
    </div>

    {/* Zone 2 — quote block + order ticket. Fixed furniture, never scrolls. */}
    <div className="shrink-0 border-t-[3px] border-gray-200 px-3 py-3">
      <SkeletonBlock width={150} height={12} />
      {/* Price + change: grey, never a number and never green/red. */}
      <SkeletonBlock width={110} height={24} className="mt-2.5" />
      <SkeletonBlock width={92} height={12} className="mt-2" />
      <SkeletonBlock width={82} height={10} className="mt-2.5" />

      {/* Sell / Buy — real chrome, inert until the quote is here. */}
      <div className="mt-4 grid grid-cols-2 gap-2">
        <button
          type="button"
          disabled
          aria-disabled="true"
          className="h-9 rounded bg-gray-100 text-gray-400 text-sm font-semibold"
        >
          Sell
        </button>
        <button
          type="button"
          disabled
          aria-disabled="true"
          className="h-9 rounded bg-gray-100 text-gray-400 text-sm font-semibold"
        >
          Buy
        </button>
      </div>

      {/* Limit / Market / Stop */}
      <div className="mt-2 grid grid-cols-3 border border-gray-200 rounded overflow-hidden">
        {["Limit", "Market", "Stop"].map((m) => (
          <button
            key={m}
            type="button"
            disabled
            aria-disabled="true"
            className="h-8 text-xs font-semibold text-gray-400"
          >
            {m}
          </button>
        ))}
      </div>

      {/* Shares / Price / Total — labels real, values grey. */}
      <div className="mt-3 grid grid-cols-3 gap-2">
        {["Shares", "Price", "Total"].map((label) => (
          <div key={label}>
            <p className="text-[11px] text-gray-400 mb-1">{label}</p>
            <SkeletonBlock height={30} rounded="rounded" />
          </div>
        ))}
      </div>

      {/* Place Order: existing unavailable state, accepts no input. */}
      <button
        type="button"
        disabled
        aria-disabled="true"
        className="mt-3 w-full h-11 rounded-lg bg-primaryColor/40 text-white text-sm font-semibold"
      >
        Place Order
      </button>
    </div>

    {/* Zone 3 — disclaimer. Static copy: always real. */}
    <div className="shrink-0 border-t border-gray-200 px-2 py-2 text-center text-black/40">
      <p className="text-[11px] leading-tight">
        This is live simulated trading with virtual cash.
      </p>
      <p className="text-[10px] leading-tight">
        No brokerage order is sent, and nothing here is financial advice.
      </p>
    </div>
  </aside>
);

/**
 * Just the chart band — instrument line + the plot area at full height.
 *
 * Passed as DesktopChartLayout's `chartSlot` so the toolbar, left tool strip,
 * bottom timeframe bar and the whole right rail (watchlist + ticket) render for
 * REAL immediately, and only the chart itself waits. Previously the entire page
 * was gated on the candles arriving, which is what made everything appear at
 * once after a blank pause.
 */
export const ChartAreaSkeleton = () => (
  <div
    className="relative w-full h-full flex flex-col"
    aria-busy="true"
    aria-label="Chart loading"
  >
    <div className="px-4 pt-2 pb-1 shrink-0">
      <SkeletonBlock width={168} height={14} />
      <SkeletonBlock width={104} height={22} className="mt-2" />
    </div>
    <div className="relative flex-1 min-h-0 px-4 pb-2">
      <SkeletonBlock width="100%" height="100%" rounded="rounded-lg" />
    </div>
  </div>
);

/**
 * `headerHidden` mirrors DesktopChartLayout's prop so the skeleton reserves the
 * same header band and the real layout doesn't shift in underneath it.
 */
const TradeSkeleton = ({ mobile = false, headerHidden = false }) => {
  if (mobile) {
    // Mobile trade: chart band + the toolbar strip beneath it. Reusable
    // primitives only, per the launch scope for Trade.
    return (
      <div aria-busy="true" aria-label="Chart loading" className="px-3 pt-3">
        <div className="flex items-center gap-3">
          <SkeletonBlock width={84} height={30} rounded="rounded-full" />
          <SkeletonBlock width={40} height={30} rounded="rounded" />
          <div className="flex-1" />
          <SkeletonBlock width={30} height={30} rounded="rounded" />
        </div>
        <SkeletonBlock width={140} height={14} className="mt-4" />
        <SkeletonBlock width={96} height={22} className="mt-2" />
        <SkeletonBlock
          height="58vh"
          rounded="rounded-lg"
          className="mt-3 w-full"
        />
        <div className="mt-3 flex items-center gap-1 overflow-hidden">
          {PERIODS.slice(0, 7).map((p) => (
            <span
              key={p}
              className="px-2.5 h-8 flex items-center rounded text-xs font-semibold text-gray-400"
            >
              {p}
            </span>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div
      aria-busy="true"
      aria-label="Chart loading"
      className="flex flex-col overflow-hidden bg-white"
      style={{
        height: headerHidden ? "100vh" : "calc(100vh - 66px)",
        marginTop: headerHidden ? 0 : "66px",
      }}
    >
      <div className="flex flex-1 min-h-0">
        <div className="flex flex-1 min-w-0 flex-col">
          <ToolbarChrome />
          <div className="flex flex-1 min-h-0">
            <LeftToolbarChrome />
            <div className="relative flex-1 min-w-0 flex flex-col">
              {/* Instrument name + symbol sit over the chart's top-left, same as
                  ChartTopSummary. Grey — the name IS data. */}
              <div className="px-4 pt-2 pb-1 shrink-0">
                <SkeletonBlock width={168} height={14} />
                <SkeletonBlock width={104} height={22} className="mt-2" />
              </div>
              {/* The chart itself fills the remaining band, so the axis and
                  bottom bar land exactly where they will sit for real. */}
              <div className="relative flex-1 min-h-0 px-4 pb-2">
                <SkeletonBlock
                  width="100%"
                  height="100%"
                  rounded="rounded-lg"
                />
              </div>
              <BottomBarChrome />
            </div>
          </div>
        </div>
        <RightRailSkeleton />
      </div>
    </div>
  );
};

export default TradeSkeleton;
